// User Roles overview.
//
// The existing "Security Role to User" tab is a flat assignment ledger, one row
// per (user, role) pair. This panel pivots the same data two ways so you can read
// it from either side:
//   - Users -> Roles: every staff member and the roles they hold.
//   - Roles -> Users: every role and the users who hold it.
// A single toggle flips between the two views, and either side can open the
// "Assign Role" modal (the row you clicked pre-selects that side's dropdown).
// Each assignment carries an optional StartDate / EndDate validity window
// (EndDate null = forever). Data comes from the same three lists the Security
// panel already loads (assignments + roles + staff).
//
// Task 24735 adds a multi-select role filter ("who has this role?"), and
// Task 24736 adds End / Remove to each chip - the screen could grant a role
// and had no way to take one back.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Badge, Col, Input, Row } from "reactstrap";
import Select from "react-select";

import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  deleteSecurityAuthRoleToUser,
  deleteSecurityAuthRoleToUserSuccess,
  getSecurityAuthRoleList,
  getSecurityAuthRoleToUserList,
  updateSecurityAuthRoleToUser,
  updateSecurityAuthRoleToUserSuccess,
} from "../../../../store/security/action";
import { getStaffList } from "../../../../store/staff/action";
import SettingsPanel from "../../components/SettingsPanel";
import TableContainer from "../../../../Components/Common/TableContainer";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import AssignSecurityRole from "../../../../Components/popUpComponents/AssignSecurityRole";

// Ids arrive as a mix of strings and numbers depending on the endpoint, so key
// every lookup by String() to keep the pivots from silently dropping matches.
const key = (v) => (v === undefined || v === null ? "" : String(v));

// Dates come back as "YYYY-MM-DD" (or a datetime). Format to dd/mm/yyyy without
// touching Date(), which would drift the day across timezones for a bare date.
const fmtDate = (v) => {
  if (!v) return null;
  const parts = String(v).slice(0, 10).split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(v);
};

// A validity window rendered for a chip: "from X", "until Y", "X to Y", or null
// when the assignment has no dates (older rows, treated as always-active).
const rangeText = (start, end) => {
  const s = fmtDate(start);
  const e = fmtDate(end);
  if (s && e) return `${s} to ${e}`;
  if (s) return `from ${s}`;
  if (e) return `until ${e}`;
  return null;
};

// Today as YYYY-MM-DD in local time. Used both as the default "end today" date
// and to tell a lapsed assignment from a live one. Built by hand rather than
// through toISOString(), which converts to UTC and can hand back yesterday.
const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Is this assignment in force today? Same rule the backend now applies
// (helper_genric___where_role_assignment_active): no dates means always active,
// and either end of the window may be open.
const isActiveNow = (start, end) => {
  const today = todayIso();
  if (start && String(start).slice(0, 10) > today) return false;
  if (end && String(end).slice(0, 10) < today) return false;
  return true;
};

// A wrapping row of chips. Each item is { label, StartDate, EndDate, id?,
// assignmentId }; the date window shows as muted text under the name. When
// `onItemClick` is given the chip becomes a clickable link (used to open the
// staff profile from a user chip).
//
// Task 24736 - each chip now carries its own End and Remove controls, because
// this screen could grant a role and had no way to take one back. An
// assignment whose window has closed is drawn greyed out and struck through
// with a "lapsed" note, so an ended role still reads as a fact that happened
// rather than quietly vanishing.
const ChipList = ({
  items,
  color,
  emptyText,
  onItemClick,
  onEnd,
  onRemove,
  busyId,
  showTitle,
}) =>
  items?.length ? (
    <div className="d-flex flex-wrap gap-2">
      {items.map((item, i) => {
        const range = rangeText(item.StartDate, item.EndDate);
        const clickable = onItemClick && item.id != null;
        const active = isActiveNow(item.StartDate, item.EndDate);
        const busy = busyId != null && busyId === item.assignmentId;
        const canAct = item.assignmentId != null;
        return (
          <span key={i} className="d-inline-flex flex-column">
            <span className="d-inline-flex align-items-center gap-1">
              <Badge
                color={active ? color : "light"}
                className={`fw-medium${clickable ? " cursor-pointer" : ""}${
                  active ? "" : " text-muted text-decoration-line-through"
                }`}
                role={clickable ? "button" : undefined}
                onClick={clickable ? () => onItemClick(item) : undefined}
              >
                {item.label}
                {/* Change #100369 - job title alongside the user name on the
                    Roles side. */}
                {showTitle && item.jobTitle && (
                  <span className="fw-normal opacity-75"> · {item.jobTitle}</span>
                )}
              </Badge>

              {/* Only offered while the role is still in force - there is
                  nothing to end about an assignment that already lapsed. */}
              {canAct && active && onEnd && (
                <button
                  type="button"
                  title="End this role - keeps the record, stops the access"
                  className="btn btn-link btn-sm p-0 text-warning"
                  disabled={busy}
                  onClick={() => onEnd(item)}
                >
                  <i className="ri-calendar-close-line align-middle"></i>
                </button>
              )}

              {canAct && onRemove && (
                <button
                  type="button"
                  title="Remove this role completely - deletes the assignment"
                  className="btn btn-link btn-sm p-0 text-danger"
                  disabled={busy}
                  onClick={() => onRemove(item)}
                >
                  <i className="ri-close-circle-line align-middle"></i>
                </button>
              )}
            </span>

            {(range || !active) && (
              <small className="text-muted mt-1" style={{ fontSize: "0.7rem" }}>
                {range}
                {!active && (range ? " · lapsed" : "lapsed")}
              </small>
            )}
          </span>
        );
      })}
    </div>
  ) : (
    <span className="text-muted">{emptyText}</span>
  );

const emptyAssign = {
  User_Id: "",
  SecurityAuthRole_Id: "",
  StartDate: "",
  EndDate: "",
};

export default function UserRoles() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("users");
  const [search, setSearch] = useState("");

  const goToStaff = useCallback(
    (id) => id != null && history.push(`/staff-details/${id}/Overview`),
    [history]
  );

  useEffect(() => {
    dispatch(getSecurityAuthRoleToUserList());
    dispatch(getSecurityAuthRoleList());
    dispatch(getStaffList());
  }, [dispatch]);

  const { assignments, roles, loading } = useSelector((state) => ({
    assignments:
      state.Security.securityAuthRoleToUserList?.securityauthroletouser_list,
    roles: state.Security.securityAuthRoleList?.securityauthrole_list,
    loading: state.Security.securityAuthRoleListLoading,
  }));
  const staff = useSelector((state) => state.Staff.staffList?.staff_list);

  // Users -> Roles: one row per staff member, carrying the roles they hold.
  const usersWithRoles = useMemo(() => {
    const roleNameById = new Map((roles || []).map((r) => [key(r.Id), r.Name]));
    return (staff || []).map((member) => {
      const roleItems = (assignments || [])
        .filter((a) => key(a.User_Id) === key(member.Id))
        .map((a) => ({
          // Task 24736 - the assignment row's own Id. Without it the chips
          // were unidentifiable and nothing could be ended or removed from
          // here; the pivot threw it away.
          assignmentId: a.Id,
          // Task 24735 - the role filter matches on id, not on the display
          // name: two roles are allowed to share a name and the pivot only
          // carried the label.
          roleId: a.SecurityAuthRole_Id,
          label: roleNameById.get(key(a.SecurityAuthRole_Id)),
          roleName: roleNameById.get(key(a.SecurityAuthRole_Id)),
          userName: member.FullName,
          StartDate: a.StartDate,
          EndDate: a.EndDate,
        }))
        .filter((r) => r.label);
      return {
        Id: member.Id,
        Name: member.FullName,
        // Change #100369 - job title under the name, so two people with
        // similar names (or a list of mostly first names) read at a glance.
        JobTitle: member.JobTitle,
        roleItems,
        roleCount: roleItems.length,
      };
    });
  }, [staff, roles, assignments]);

  // Roles -> Users: one row per role, carrying the users who hold it.
  const rolesWithUsers = useMemo(() => {
    const staffNameById = new Map(
      (staff || []).map((s) => [key(s.Id), s.FullName])
    );
    const staffTitleById = new Map(
      (staff || []).map((s) => [key(s.Id), s.JobTitle])
    );
    return (roles || []).map((role) => {
      const userItems = (assignments || [])
        .filter((a) => key(a.SecurityAuthRole_Id) === key(role.Id))
        .map((a) => ({
          id: a.User_Id,
          assignmentId: a.Id,
          label: staffNameById.get(key(a.User_Id)),
          jobTitle: staffTitleById.get(key(a.User_Id)),
          userName: staffNameById.get(key(a.User_Id)),
          roleName: role.Name,
          StartDate: a.StartDate,
          EndDate: a.EndDate,
        }))
        .filter((u) => u.label);
      return {
        Id: role.Id,
        Name: role.Name,
        userItems,
        userCount: userItems.length,
      };
    });
  }, [staff, roles, assignments]);

  // ---- Role filter (Task 24735) -----------------------------------------
  //
  // "Filter by a role to see who has it", and it takes more than one. The
  // multi-select answers two different questions depending on the mode:
  //   ANY (default) - holds at least one of the picked roles. The plain
  //                   reading of "who has it", widened to several.
  //   ALL           - holds every one of them. The audit question: who is
  //                   both an Approver and a Manager?
  // The toggle only appears once a second role is picked, because with one
  // role the two modes are the same thing and the control would be noise.
  //
  // Ended assignments do NOT count as holding a role. "Who has it" is present
  // tense, and since Task 24736 an ended assignment genuinely no longer grants
  // anything - so counting one here would name people who cannot do the thing.
  // "Include ended" is there for the other question ("who used to have it?").
  const [roleFilter, setRoleFilter] = useState([]);
  const [matchAll, setMatchAll] = useState(false);
  const [includeEnded, setIncludeEnded] = useState(false);

  const roleOptions = useMemo(
    () =>
      (roles || [])
        .map((r) => ({ value: key(r.Id), label: r.Name }))
        .sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    [roles]
  );

  const selectedRoleIds = useMemo(
    () => roleFilter.map((o) => key(o.value)),
    [roleFilter]
  );

  const clearFilters = () => {
    setSearch("");
    setRoleFilter([]);
    setMatchAll(false);
    setIncludeEnded(false);
  };

  // Name search + role filter over whichever side is active.
  const filtered = useMemo(() => {
    const source = activeTab === "users" ? usersWithRoles : rolesWithUsers;
    const term = search.trim().toLowerCase();

    let rows = source;

    if (term) {
      // The users side also matches on job title (Change #100369), so a
      // search for "engineer" finds the engineers.
      rows = rows.filter(
        (row) =>
          row.Name?.toLowerCase().includes(term) ||
          row.JobTitle?.toLowerCase().includes(term)
      );
    }

    if (selectedRoleIds.length) {
      if (activeTab === "users") {
        rows = rows.filter((row) => {
          const held = new Set(
            (row.roleItems || [])
              .filter(
                (item) =>
                  includeEnded || isActiveNow(item.StartDate, item.EndDate)
              )
              .map((item) => key(item.roleId))
          );
          return matchAll
            ? selectedRoleIds.every((id) => held.has(id))
            : selectedRoleIds.some((id) => held.has(id));
        });
      } else {
        // On the Roles side the filter is simply "show me these roles".
        // matchAll has no meaning here - a row IS one role - so it is ignored
        // rather than silently emptying the table.
        rows = rows.filter((row) => selectedRoleIds.includes(key(row.Id)));
      }
    }

    return rows;
  }, [
    activeTab,
    search,
    usersWithRoles,
    rolesWithUsers,
    selectedRoleIds,
    matchAll,
    includeEnded,
  ]);

  const filtersActive = !!search.trim() || selectedRoleIds.length > 0;

  // ---- Assign Role modal (shared popup) ---------------------------------
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPreset, setAssignPreset] = useState({});

  const openAssign = (preset = {}) => {
    setAssignPreset(preset);
    setAssignOpen(true);
  };

  // ---- End / remove an assignment (Task 24736) --------------------------
  //
  // Two different things, deliberately kept apart:
  //
  //   End    - writes an EndDate. The assignment stays, the history says the
  //            person held the role and when they stopped. Access is revoked
  //            because the backend now honours the window.
  //   Remove - deletes the row. Use it for a mistake, not for a leaver.
  //
  // End defaults to today and is confirmed in a small modal that lets the date
  // be moved (so a future end date can be scheduled). Remove goes through the
  // shared DeleteModal, because it is destructive and cannot be undone.
  const [endTarget, setEndTarget] = useState(null);
  const [endDate, setEndDate] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [busyAssignmentId, setBusyAssignmentId] = useState(null);

  const openEnd = (item) => {
    setEndTarget(item);
    // Pre-fill with the existing end date if one is already set (this then
    // reads as "change when it ends"), otherwise today.
    setEndDate(
      item.EndDate ? String(item.EndDate).slice(0, 10) : todayIso()
    );
  };

  const confirmEnd = () => {
    if (!endTarget?.assignmentId) return;
    setBusyAssignmentId(endTarget.assignmentId);
    dispatch(
      updateSecurityAuthRoleToUser([
        { Id: endTarget.assignmentId, EndDate: endDate },
      ])
    );
    setEndTarget(null);
  };

  const confirmRemove = () => {
    if (!removeTarget?.assignmentId) return;
    setBusyAssignmentId(removeTarget.assignmentId);
    dispatch(deleteSecurityAuthRoleToUser([removeTarget.assignmentId]));
    setRemoveTarget(null);
  };

  const { endResponse, removeResponse } = useSelector((state) => ({
    endResponse: state.Security.securityAuthRoleToUserUpdateSuccess,
    removeResponse: state.Security.securityAuthRoleToUserDeleteSuccess,
  }));

  useEffect(() => {
    if (isEmpty(endResponse)) return;
    if (endResponse.status === 200) {
      toast.success("Role ended");
      dispatch(getSecurityAuthRoleToUserList());
    } else {
      toast.error(endResponse.message || "Could not end the role");
    }
    setBusyAssignmentId(null);
    dispatch(updateSecurityAuthRoleToUserSuccess([]));
  }, [endResponse, dispatch]);

  useEffect(() => {
    if (isEmpty(removeResponse)) return;
    if (removeResponse.status === 200) {
      toast.success("Role removed");
      dispatch(getSecurityAuthRoleToUserList());
    } else {
      toast.error(removeResponse.message || "Could not remove the role");
    }
    setBusyAssignmentId(null);
    dispatch(deleteSecurityAuthRoleToUserSuccess([]));
  }, [removeResponse, dispatch]);

  // ---- Columns ----------------------------------------------------------
  const userColumns = useMemo(
    () => [
      {
        Header: "User",
        accessor: "Name",
        filterable: false,
        Cell: (card) => (
          <div
            className="fw-medium like-href cursor-pointer"
            role="button"
            onClick={() => goToStaff(card.row.original?.Id)}
          >
            {card.row.original?.Name}
            {card.row.original?.JobTitle && (
              <div className="text-muted small fw-normal">
                {card.row.original.JobTitle}
              </div>
            )}
          </div>
        ),
      },
      {
        Header: "Roles",
        accessor: "roleItems",
        filterable: false,
        Cell: (card) => (
          <ChipList
            items={card.row.original?.roleItems}
            color="primary"
            emptyText="No roles assigned"
            onEnd={openEnd}
            onRemove={setRemoveTarget}
            busyId={busyAssignmentId}
          />
        ),
      },
      {
        Header: "# Roles",
        accessor: "roleCount",
        filterable: false,
        Cell: (card) => (
          <Badge color="light" className="text-body">
            {card.row.original?.roleCount}
          </Badge>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (card) => (
          <button
            className="btn btn-sm btn-soft-success"
            onClick={() => openAssign({ User_Id: card.row.original?.Id })}
          >
            <i className="ri-add-line align-bottom me-1"></i>
            Assign role
          </button>
        ),
      },
    ],
    // busyAssignmentId so the chip buttons re-render disabled while a save is
    // in flight; openEnd/setRemoveTarget are stable enough to leave out.
    [goToStaff, busyAssignmentId]
  );

  const roleColumns = useMemo(
    () => [
      {
        Header: "Role",
        accessor: "Name",
        filterable: false,
        Cell: (card) => (
          <div className="fw-medium">{card.row.original?.Name}</div>
        ),
      },
      {
        Header: "Users",
        accessor: "userItems",
        filterable: false,
        Cell: (card) => (
          <ChipList
            items={card.row.original?.userItems}
            color="info"
            emptyText="No users assigned"
            onItemClick={(item) => goToStaff(item.id)}
            showTitle
            onEnd={openEnd}
            onRemove={setRemoveTarget}
            busyId={busyAssignmentId}
          />
        ),
      },
      {
        Header: "# Users",
        accessor: "userCount",
        filterable: false,
        Cell: (card) => (
          <Badge color="light" className="text-body">
            {card.row.original?.userCount}
          </Badge>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (card) => (
          <button
            className="btn btn-sm btn-soft-success"
            onClick={() =>
              openAssign({ SecurityAuthRole_Id: card.row.original?.Id })
            }
          >
            <i className="ri-add-line align-bottom me-1"></i>
            Assign user
          </button>
        ),
      },
    ],
    // busyAssignmentId so the chip buttons re-render disabled while a save is
    // in flight; openEnd/setRemoveTarget are stable enough to leave out.
    [goToStaff, busyAssignmentId]
  );

  const tabs = [
    {
      value: "users",
      label: "Users → Roles",
      icon: "fa-solid fa-users",
      count: usersWithRoles.length,
    },
    {
      value: "roles",
      label: "Roles → Users",
      icon: "fa-solid fa-shield-halved",
      count: rolesWithUsers.length,
    },
  ];

  return (
    <SettingsPanel
      title="User Roles"
      description="See every user and the roles they hold, or flip it around to see every role and its users. Filter by one or more roles to answer who has them, and end or remove a role from the chip itself."
      tabs={tabs}
      value={activeTab}
      onChange={(value) => {
        setActiveTab(value);
        // The name search means a different thing on each side (users vs
        // roles), so it is cleared on the flip as it always has been. The role
        // filter is NOT cleared - it means the same thing on both sides, and
        // carrying it over is what makes "flip it around" useful: filter to a
        // role on the Users tab, flip, and you are looking at that role and
        // everyone who holds it.
        setSearch("");
      }}
    >
      {assignOpen && (
        <AssignSecurityRole
          open={assignOpen}
          toggle={() => setAssignOpen(false)}
          preset={assignPreset}
        />
      )}

      {/* Task 24736 - End. A date, not a deletion: the assignment survives and
          the record still shows the role was held. Defaults to today, but the
          date is editable so an end can be scheduled ahead of a leaver's last
          day. */}
      {endTarget && (
        <AppModal
          open={!!endTarget}
          onClose={() => setEndTarget(null)}
          title="End this role"
          icon="ri-calendar-close-line"
          onSubmit={confirmEnd}
          submitLabel="End role"
          submitColor="warning"
          submitDisabled={!endDate}
        >
          <p className="mb-3">
            <strong>{endTarget.roleName}</strong> for{" "}
            <strong>{endTarget.userName}</strong>.
          </p>
          <div className="mb-2">
            <label className="form-label">Last day the role applies</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <small className="text-muted">
            The assignment is kept, so the record still shows this role was
            held and when it stopped. Access ends after this date. To wipe the
            assignment entirely, use Remove instead.
          </small>
        </AppModal>
      )}

      {/* Remove. Destructive and unrecoverable, so it keeps the shared
          confirmation the rest of the app uses. */}
      {removeTarget && (
        <DeleteModal
          show={!!removeTarget}
          onDeleteClick={confirmRemove}
          onCloseClick={() => setRemoveTarget(null)}
        />
      )}

      <div className="p-2 m-2">
        <Row className="g-4 align-items-center mb-2">
          <Col sm={3}>
            <div className="search-box">
              <Input
                type="search"
                className="form-control search"
                placeholder={
                  activeTab === "users" ? "Search users..." : "Search roles..."
                }
                onChange={(e) => setSearch(e.target.value)}
                value={search}
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </Col>

          {/* Task 24735 - filter by role. Multi-select, because the question
              is rarely about exactly one role. */}
          <Col sm={4}>
            <Select
              isMulti
              isClearable
              options={roleOptions}
              value={roleFilter}
              onChange={(value) => setRoleFilter(value || [])}
              placeholder={
                activeTab === "users"
                  ? "Filter by role - who has it?"
                  : "Filter by role..."
              }
              classNamePrefix="select"
              noOptionsMessage={() => "No roles"}
            />
          </Col>

          {/* Only meaningful on the Users side (a row on the Roles side IS one
              role), and only once a second role makes the two modes differ. */}
          {activeTab === "users" && roleFilter.length > 1 && (
            <div className="col-sm-auto">
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${
                    !matchAll ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setMatchAll(false)}
                  title="Users holding at least one of the selected roles"
                >
                  Any
                </button>
                <button
                  type="button"
                  className={`btn ${
                    matchAll ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setMatchAll(true)}
                  title="Only users holding every selected role"
                >
                  All
                </button>
              </div>
            </div>
          )}

          {activeTab === "users" && roleFilter.length > 0 && (
            <div className="col-sm-auto">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="userRolesIncludeEnded"
                  checked={includeEnded}
                  onChange={(e) => setIncludeEnded(e.target.checked)}
                />
                <label
                  className="form-check-label text-muted"
                  htmlFor="userRolesIncludeEnded"
                  title="By default an ended role does not count as holding it - tick this to find who used to have it"
                >
                  Include ended
                </label>
              </div>
            </div>
          )}

          <div className="col-sm-auto ms-auto d-flex gap-2">
            {filtersActive && (
              <button
                className="btn btn-outline-secondary"
                onClick={clearFilters}
              >
                <i className="ri-filter-off-line align-bottom me-1"></i>
                Clear
              </button>
            )}
            <button
              className="btn btn-success"
              onClick={() => openAssign()}
            >
              <i className="ri-add-line align-bottom me-1"></i>
              Assign Role
            </button>
          </div>
        </Row>

        {/* Say what the filter did, so an empty table reads as "nobody holds
            this" rather than "the screen is broken". */}
        {filtersActive && (
          <div className="text-muted small mb-2">
            {filtered.length}{" "}
            {activeTab === "users"
              ? filtered.length === 1
                ? "user"
                : "users"
              : filtered.length === 1
              ? "role"
              : "roles"}{" "}
            {activeTab === "users" && roleFilter.length > 0 && (
              <>
                holding {matchAll && roleFilter.length > 1 ? "all" : "any"} of:{" "}
                {roleFilter.map((o) => o.label).join(", ")}
                {!includeEnded && " (ended roles not counted)"}
              </>
            )}
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : (
          <TableContainer
            columns={activeTab === "users" ? userColumns : roleColumns}
            data={filtered || []}
            isGlobalFilter={false}
            isAddUserList={false}
            customPageSize={10}
            className="custom-header-css"
            divClass="table-responsive table-card mb-0"
            tableClass="align-middle"
            theadClass="text-muted"
            isLeadsFilter={false}
          />
        )}
      </div>
    </SettingsPanel>
  );
}
